// Trip sharing v1 — API client for both sides of a shared trip.
//
// Owner side (share dialog): GET/PUT /api/trips/:id/share via the authApi
// instance (JWT or guest token — guests can share, spec §7).
//
// Visitor side (/s/<token> pages): the public /api/public/shared-trips/*
// endpoints via a PLAIN axios instance. Deliberately not authApi: its 401
// interceptor opens the guest signup wall, which would swallow the
// PASSWORD_REQUIRED response of a password-protected share.
//
// Backend: backend/routes/trips.js (owner), backend/routes/publicShare.js
// (visitor), backend/services/trips/tripShareService.js (rules + stripping).

import axios from 'axios';
import { authApi } from '../auth/api';
import { getGuestToken } from '../auth/guest';
import { TripDetail } from '../my-trips/types';
import type { ShareEmailResult } from '../../shared/share/ShareChannels';
import { OmioRoute, resolveOmioRoute, omioPositionParams } from '../journeys/omioPositions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

const shareApi = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type ShareVisibility = 'private' | 'link' | 'password';

export interface ShareState {
  visibility: ShareVisibility;
  includeBookings: boolean;
  /** Path form of the share link (/s/<token>) — compose with the current
   *  origin so local dev links stay local. Null while private. */
  sharePath: string | null;
  /** Absolute URL built server-side (prod base) — used as fallback. */
  shareUrl: string | null;
  password: string | null;
  sharedAt: string | null;
}

export interface SharedTripMeta {
  visibility: Exclude<ShareVisibility, 'private'>;
  includeBookings: boolean;
  ownerFirstName: string | null;
  tripTitle: string;
}

export interface SharedTripPayload {
  share: SharedTripMeta;
  trip: TripDetail;
}

export interface SharedJourneyPayload {
  share: SharedTripMeta;
  journey: any; // v2 journey shape, stripped (see backend sanitizePublicJourney)
}

/** Error classification for the visitor pages' state machine. */
export type SharedAccessProblem =
  | 'password_required'
  | 'password_incorrect'
  | 'too_many_attempts'
  | 'expired'
  | 'network'
  | 'not_found';

export function classifyShareError(e: any): SharedAccessProblem {
  const status = e?.response?.status;
  const code = e?.response?.data?.code;
  if (code === 'PASSWORD_REQUIRED') return 'password_required';
  if (code === 'PASSWORD_INCORRECT') return 'password_incorrect';
  if (status === 429) return 'too_many_attempts';
  if (status === 410 || code === 'SHARE_EXPIRED') return 'expired';
  // No HTTP response at all (offline, blocked, server down): retryable, and
  // must NOT render as the terminal "link is not available" card — that once
  // masked a CORS-blocked password request as a dead link.
  if (!e?.response) return 'network';
  return 'not_found';
}

// ─────────────────────────────────────────────────────────────────────────────
// Owner side
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchShareSettings(tripId: string): Promise<ShareState> {
  const res = await authApi.get(`/trips/${tripId}/share`);
  return res.data;
}

export async function updateShareSettings(
  tripId: string,
  patch: { visibility?: ShareVisibility; includeBookings?: boolean },
): Promise<ShareState> {
  const res = await authApi.put(`/trips/${tripId}/share`, patch);
  return res.data;
}

/** Path suffix that makes a trip's share link open one of its journeys
 *  (/s/<token>/journeys/<id>). Access stays trip-level either way. */
export const sharedJourneySuffix = (journeyId?: string | null): string =>
  (journeyId ? `/journeys/${encodeURIComponent(journeyId)}` : '');

/**
 * The copy-paste share link, preferring the current origin (works in dev).
 * Given a journeyId, the link opens that journey instead of the trip page
 * (shared from a journey detail page, Paulo 2026-10-03).
 */
export function composeShareUrl(
  state: Pick<ShareState, 'sharePath' | 'shareUrl'>,
  journeyId?: string | null,
): string | null {
  const suffix = sharedJourneySuffix(journeyId);
  if (state.sharePath && typeof window !== 'undefined') {
    return `${window.location.origin}${state.sharePath}${suffix}`;
  }
  return state.shareUrl ? `${state.shareUrl}${suffix}` : null;
}

/**
 * "Send by email" (owner, signed-in accounts only): Jody emails the share
 * link in the owner's name. `message` is the sender's optional note (not the
 * dialog's chat message: the email builds its own links). Backend:
 * routes/trips.js POST /:id/share/email, services/trips/tripShareEmail.js.
 */
export async function sendShareEmail(
  tripId: string,
  body: { to: string[]; message: string; journeyId?: string | null },
): Promise<ShareEmailResult> {
  const res = await authApi.post(`/trips/${tripId}/share/email`, body);
  return res.data;
}

/**
 * The canonical share message. Mirrors the owner-notification email
 * (backend/services/emailTemplates.js getTripSharedTemplate) so the copy the
 * user pastes and the copy they forward read the same. Editable in the dialog.
 */
export function buildShareMessage(opts: { tripTitle: string; url: string; password?: string | null }): string {
  const base = `I want to show you a trip I'm planning on Jody: "${opts.tripTitle}". Open it here: ${opts.url}`;
  return opts.password ? `${base}\nPassword: ${opts.password}` : base;
}

// ─────────────────────────────────────────────────────────────────────────────
// Visitor side
// ─────────────────────────────────────────────────────────────────────────────

// The entered password for a token, kept for this browser session so
// navigating between the trip and its journeys doesn't re-ask.
const pwKey = (token: string) => `jodySharePw:${token}`;
export const getStoredSharePassword = (token: string): string | null => {
  try { return sessionStorage.getItem(pwKey(token)); } catch { return null; }
};
export const storeSharePassword = (token: string, password: string) => {
  try { sessionStorage.setItem(pwKey(token), password); } catch { /* private mode etc. */ }
};

const passwordHeaders = (token: string) => {
  const password = getStoredSharePassword(token);
  return password ? { 'X-Share-Password': password } : {};
};

export async function fetchSharedTrip(token: string): Promise<SharedTripPayload> {
  const res = await shareApi.get(`/public/shared-trips/${encodeURIComponent(token)}`, {
    headers: passwordHeaders(token),
  });
  return res.data;
}

export async function fetchSharedJourney(token: string, journeyId: string): Promise<SharedJourneyPayload> {
  const res = await shareApi.get(
    `/public/shared-trips/${encodeURIComponent(token)}/journeys/${encodeURIComponent(journeyId)}`,
    { headers: passwordHeaders(token) },
  );
  return res.data;
}

// Booking links for visitors (product backlog #51): the shared view's twins
// of the journey's own link routes (features/journeys/api.ts). Called at the
// moment of a click. Backend: routes/publicShare.js.
const isoDay = (value?: string | null): string | undefined =>
  (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined);
const sharedJourneyPath = (token: string, journeyId: string) =>
  `/public/shared-trips/${encodeURIComponent(token)}/journeys/${encodeURIComponent(journeyId)}`;

// `route` (the leg's two stops): looked up in Omio's suggester first, as the
// owner's fetchers do (features/journeys/omioPositions.ts), so the visitor's
// link is Omio's pre-filled search too.
export async function fetchSharedLegOmioLink(
  token: string, journeyId: string, legId: string, ctx?: { date?: string | null; route?: OmioRoute | null },
): Promise<{ url: string }> {
  const positions = omioPositionParams(await resolveOmioRoute(ctx?.route));
  const res = await shareApi.get(`${sharedJourneyPath(token, journeyId)}/legs/${encodeURIComponent(legId)}/affiliate/omio`, {
    headers: passwordHeaders(token),
    params: { date: isoDay(ctx?.date), ...positions },
  });
  return res.data;
}

export async function fetchSharedNightTrainBookingLink(
  token: string, journeyId: string, legId: string, ctx?: { date?: string | null; route?: OmioRoute | null },
): Promise<{ url: string; channel: 'omio' | 'operator'; operatorKey: string | null; operatorName: string | null }> {
  const positions = omioPositionParams(await resolveOmioRoute(ctx?.route));
  const res = await shareApi.get(`${sharedJourneyPath(token, journeyId)}/legs/${encodeURIComponent(legId)}/night-train/booking`, {
    headers: passwordHeaders(token),
    params: { date: isoDay(ctx?.date), ...positions },
  });
  return res.data;
}

export async function fetchSharedStopBookingLink(
  token: string, journeyId: string, stopId: string, ctx?: { checkin?: string | null; checkout?: string | null },
): Promise<{ url: string }> {
  const res = await shareApi.get(`${sharedJourneyPath(token, journeyId)}/stops/${encodeURIComponent(stopId)}/affiliate/booking`, {
    headers: passwordHeaders(token),
    params: { checkin: isoDay(ctx?.checkin), checkout: isoDay(ctx?.checkout) },
  });
  return res.data;
}

/**
 * "Save a copy" — signed-in visitors only (401 for guests/anonymous).
 * Copies the WHOLE trip (every alternative, deterministic rows); the
 * journeyIds map (source id → copy id) lets the caller land the visitor on
 * the alternative they came from.
 */
export async function saveSharedCopy(
  token: string,
  title: string,
): Promise<{ tripId: string; mainJourneyId: string; journeyIds: Record<string, string>; alternativesCopied: number }> {
  const res = await authApi.post(
    `/public/shared-trips/${encodeURIComponent(token)}/copy`,
    { title },
    { headers: passwordHeaders(token) },
  );
  return res.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// App URL ↔ share link (Paulo 2026-10-03)
// ─────────────────────────────────────────────────────────────────────────────
// The owner opening their own /s/ link goes to their editable page; anyone
// else opening an app URL (/trips/<id>, /journeys/<id>) of a shared trip goes
// to its /s/ page. Both return null on any failure: the caller then carries on
// as before (visitor view / login / not-available card).

const isAppPath = (path: unknown): path is string =>
  typeof path === 'string' && /^\/(trips|journeys|s)\/[^/]/.test(path);

/** The session as headers, for the plain instance: a stale session must not
 *  open the guest wall or the refresh flow on a share page. */
function sessionHeaders(): Record<string, string> {
  const jwt = localStorage.getItem('authToken');
  if (jwt) return { Authorization: `Bearer ${jwt}` };
  const guestToken = getGuestToken();
  return guestToken ? { 'X-Guest-Token': guestToken } : {};
}

/** The owner's own page for a share link, or null when the viewer isn't the owner. */
export async function fetchOwnerPath(token: string, journeyId?: string | null): Promise<string | null> {
  try {
    const res = await shareApi.get(`/public/shared-trips/${encodeURIComponent(token)}/owner-path`, {
      headers: sessionHeaders(),
      params: { journey: journeyId || undefined },
    });
    return isAppPath(res.data?.path) ? res.data.path : null;
  } catch {
    return null;
  }
}

/** The /s/ page of a trip or journey, or null when its trip isn't shared. */
export async function fetchSharePath(target: { tripId: string } | { journeyId: string }): Promise<string | null> {
  try {
    const res = await shareApi.get('/public/shared-trips/lookup', {
      params: 'tripId' in target ? { trip: target.tripId } : { journey: target.journeyId },
    });
    return isAppPath(res.data?.path) ? res.data.path : null;
  } catch {
    return null;
  }
}

/** "Send by email" for a SIGNED-IN visitor resharing the trip (same service
 *  and limits as the owner's). Backend: routes/publicShare.js POST /:token/email. */
export async function sendSharedTripEmail(
  token: string,
  body: { to: string[]; message: string; journeyId?: string | null },
): Promise<ShareEmailResult> {
  const res = await authApi.post(
    `/public/shared-trips/${encodeURIComponent(token)}/email`,
    body,
    { headers: passwordHeaders(token) },
  );
  return res.data;
}

/** Edit-access request (V2 desirability probe) — signed-in visitors only. */
export async function requestEditAccess(token: string): Promise<void> {
  await authApi.post(
    `/public/shared-trips/${encodeURIComponent(token)}/edit-request`,
    {},
    { headers: passwordHeaders(token) },
  );
}
